import { strandMeaning } from '@/lib/teaching/strands';
import { useEffect, useState } from 'preact/hooks';
import { useLocation, useSearch } from 'wouter-preact';
import type { Analysis as AnalysisT, BucketRow, ItemTableRow, WeakState } from '@teacher-shared/analysis';
import { api, type Dataset } from '@/lib/teaching/teacher-api';
import { invalidateDataset, loadDataset } from '@/lib/teaching/state';
import { errText } from '@/lib/teaching/slice2';
import { FINALISED_TEXT, type AnalysisTabId, type NoteView } from '@/lib/teaching/slice4';
import { TeachingPage, Card, ClassSwitcher, useClass, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, INK, pct, CodeTag } from '@/components/teaching/ui';
import { Btn, ErrorLine } from '@/components/teaching/form';
import { Tabs, textareaCls } from '@/components/teaching/setup';
import { KpiRow, KpiCard, BarCell, Chip, TableCard, thCls, type ChipTone } from '@/components/teaching/kit';

// Analysis tables (slice 4 §07, SOP §33-§36; U9, U18, U19, U21, U23).
// All client-side from the dataset through the shared analysis module, the
// same pure functions the server's QC runs.

const TABS: { id: AnalysisTabId; label: string }[] = [
  { id: 'item', label: 'Question' }, { id: 'strand', label: 'Strand' }, { id: 'topic', label: 'Topic' }, { id: 'command', label: 'Command term' },
];

const WEAK_LOOK: Record<WeakState, { label: string; tone: ChipTone }> = {
  weak: { label: 'Weak', tone: 'reteach' },
  not_weak: { label: 'Not weak', tone: 'strong' },
  not_enough_evidence: { label: 'Too few answers to say', tone: 'neutral' },
};

export function Analysis() {
  return <WithData state={useAnalysis()} title="Analysis">{({ a, A, ds }) => <AnalysisView a={a} A={A} ds={ds} />}</WithData>;
}

function Rate({ rate }: { rate: number | null }) {
  return <BarCell rate={rate} label={rate === null ? '' : undefined} min={120} />;
}

const th = thCls;
const stickyTh = 'sticky left-0 z-10 ' + th;
const stickyTd = 'sticky left-0 z-10 bg-[var(--color-card)] px-3 py-2 font-semibold text-[var(--color-text)] whitespace-nowrap';
const td = 'px-3 py-2 tabular-nums text-[var(--color-text)]';

function ItemTable({ rows, meaning }: { rows: ItemTableRow[]; meaning: Record<string, string> }) {
  return (
    <TableCard>
      <table class="border-collapse text-[12.5px] w-full" data-analysis-table="item">
        <thead><tr>
          <th class={stickyTh}>No.</th><th class={th}>Question</th><th class={th} title="Answers that could be marked">Marked</th><th class={th} title="Average mark">Average</th><th class={th}>Out of</th>
          <th class={th} title="Average mark as a share of the maximum">Marks earned</th><th class={th} title="Share of students who reached the mastery mark">Mastery</th><th class={th}>Common mistakes</th><th class={th}></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.item.id} class="border-t border-[var(--color-border)]" data-item-row={r.item.id}>
              <td class={stickyTd}>{r.item.id}</td>
              <td class="px-3 py-2 text-[var(--color-text)] min-w-[220px] max-w-[360px]">{r.item.prompt}</td>
              <td class={td}>{r.valid}</td>
              <td class={td}>{r.meanMark === null ? '' : r.meanMark.toFixed(1)}</td>
              <td class={td}>{r.max}</td>
              <td class="px-3 py-2"><Rate rate={r.success} /></td>
              <td class="px-3 py-2"><Rate rate={r.r.rate} /></td>
              <td class="px-3 py-2 text-[var(--color-text-muted)] min-w-[180px]">{r.topCodes.map((c) => <div key={c.code}>{meaning[c.code] ?? c.code} ({c.count})<CodeTag code={c.code} /></div>)}</td>
              <td class="px-3 py-2">{r.rank && <Chip tone={r.rank === 'strongest' ? 'strong' : 'reteach'} title={r.rank === 'strongest' ? 'Strongest question' : 'Weakest question'}>{r.rank === 'strongest' ? 'Strong' : 'Reteach'}</Chip>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}

function BucketTable({ rows, name }: { rows: BucketRow[]; name: string }) {
  return (
    <TableCard>
      <table class="border-collapse text-[12.5px] w-full" data-analysis-table={name}>
        <thead><tr>
          <th class={stickyTh}>{name === 'command' ? 'Command term' : name[0].toUpperCase() + name.slice(1)}</th><th class={th}>Answers</th><th class={th}>Mastery</th>
          <th class={th}>Questions</th><th class={th}>Status</th><th class={th} title="Students under 50% mastery here, on 2 or more questions">Students under 50%</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} class="border-t border-[var(--color-border)] align-top">
              <td class={stickyTd}>{name === 'strand' ? <span title={strandMeaning(r.label)}>Strand {r.label}{strandMeaning(r.label) && <span class="hidden md:inline font-normal text-[var(--color-text-muted)]">: {strandMeaning(r.label)}</span>}</span> : r.label}</td>
              <td class={td}>{r.r.n}<span class="text-[11px] text-[var(--color-text-faint)]"> · {r.r.students} students</span></td>
              <td class="px-3 py-2"><Rate rate={r.r.rate} /></td>
              <td class="px-3 py-2 text-[var(--color-text-muted)] whitespace-nowrap">{r.items.join(', ')}</td>
              <td class="px-3 py-2"><Chip tone={WEAK_LOOK[r.weak].tone}>{WEAK_LOOK[r.weak].label}</Chip></td>
              <td class="px-3 py-2 text-[var(--color-text-muted)] min-w-[200px]">{r.under50.length ? r.under50.map((s) => s.name).join(', ') : 'none'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}

function NoteEditor({ a, tab, note, finalised }: { a: string; tab: AnalysisTabId; note: NoteView | undefined; finalised: boolean }) {
  const [text, setText] = useState(note?.text ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(note?.text ?? ''); }, [note?.revision, tab]);
  async function run(confirm: boolean) {
    setErr(null); setBusy(true);
    try {
      if (confirm) await api('POST', `/api/t/assessments/${a}/analysis-notes/${tab}/confirm`, { expectedRevision: note?.revision ?? 0 });
      else await api('PUT', `/api/t/assessments/${a}/analysis-notes/${tab}`, { text, expectedRevision: note?.revision ?? 0 });
      invalidateDataset(a); await loadDataset(a, true);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }
  return (
    <Card title="What this means for teaching" sub="Your note for this tab. Checkpoint 3 needs one for every tab that has data.">
      {note?.stale && <div class="rounded-lg px-2 py-1.5 mb-2 text-[12.5px]" style={{ background: '#fff6e0', color: INK }} data-note-stale><b>{note.stale}.</b> Re-save the note or confirm it is still true.</div>}
      <textarea aria-label={`Instructional implication for ${tab}`} class={textareaCls} rows={3} value={text} disabled={finalised}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} placeholder="What will you teach differently because of this table?" />
      <div class="flex flex-wrap items-center gap-2 mt-2">
        <Btn kind="primary" disabled={finalised || busy || !text.trim() || text === note?.text} title={finalised ? FINALISED_TEXT : undefined} onClick={() => run(false)}>Save note</Btn>
        {note?.stale && <Btn disabled={finalised || busy} title={finalised ? FINALISED_TEXT : undefined} onClick={() => run(true)}>Still true, confirm</Btn>}
        {finalised && <span class="text-[12px] text-[var(--color-text-muted)]">{FINALISED_TEXT}</span>}
        {note && !note.stale && <span class="text-[11.5px] text-[var(--color-text-faint)]">Saved by {note.updated_by}</span>}
      </div>
      <ErrorLine error={err} />
    </Card>
  );
}

function AnalysisView({ a, A, ds }: { a: string; A: AnalysisT; ds: Dataset }) {
  const [cls, setCls] = useClass(A.classes);
  const search = useSearch();
  const [loc, navigate] = useLocation();
  const raw = new URLSearchParams(search).get('tab');
  const tab: AnalysisTabId = TABS.some((t) => t.id === raw) ? raw as AnalysisTabId : 'item';
  const setTab = (t: AnalysisTabId) => { const p = new URLSearchParams(search); p.set('tab', t); navigate(`${loc}?${p}`, { replace: true }); };
  const res = A.tab(tab, cls);
  const finalised = ds.assessment.status === 'finalised';
  const note = ds.analysis_notes?.find((n) => n.tab === tab);
  const rated = res.rows.filter((r) => r.r.rate !== null);
  const tot = res.rows.reduce((acc, r) => ({ m: acc.m + r.r.mastered, n: acc.n + r.r.n }), { m: 0, n: 0 });
  const ranked = (k: 'strongest' | 'weakest') => tab === 'item' ? (res.rows as ItemTableRow[]).find((r) => r.rank === k) : undefined;
  const weakest = ranked('weakest');
  const strongest = ranked('strongest');
  const weakBuckets = tab === 'item' ? [] : (res.rows as BucketRow[]).filter((r) => r.weak === 'weak');
  const noun = tab === 'item' ? 'questions' : tab === 'strand' ? 'strands' : tab === 'topic' ? 'topics' : 'command terms';

  return (
    <TeachingPage title="Analysis" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />}
      actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <Tabs label="Analysis tab" value={tab} options={TABS} onChange={setTab} />
      {res.applicable && (
        <KpiRow>
          <KpiCard value={tot.n ? pct(tot.m / tot.n) : '-'} label="Mastered overall" hint={`${tot.m} of ${tot.n} marked answers`} />
          {tab === 'item' ? (
            <>
              <KpiCard value={weakest ? weakest.item.id : '-'} label="Weakest question" hint={weakest ? `${pct(weakest.r.rate)} mastered` : res.comparativeReason ?? undefined} />
              <KpiCard value={strongest ? strongest.item.id : '-'} label="Strongest question" hint={strongest ? `${pct(strongest.r.rate)} mastered` : res.comparativeReason ?? undefined} />
            </>
          ) : (
            <>
              <KpiCard value={weakBuckets.length} label={`Weak ${noun}`} hint={weakBuckets.map((b) => b.label).join(', ') || 'none with enough marked answers'} />
              <KpiCard value={res.diagnostics.length} label="Likely causes" hint="one line per weak area, below" />
            </>
          )}
          <KpiCard value={`${rated.length}/${res.rows.length}`} label={`${noun[0].toUpperCase() + noun.slice(1)} with marked answers`} />
        </KpiRow>
      )}
      {!res.applicable ? (
        <Card><div class="text-[13px] font-medium" style={{ color: 'var(--color-text)' }} data-not-applicable>{res.reason}</div></Card>
      ) : (
        <>
          <div class="text-[12.5px] text-[var(--color-text-muted)]">
            {tab === 'item' ? 'Sorted from most to least mastered. Mastery = the student reached the mark you set for that question. Marks earned = average mark out of the maximum.'
              : `Weak = under 50% mastery, based on at least 10 marked answers, 2 or more questions and enough students in the class. Anything smaller is "too few answers to say".`}
            {' '}{rated.length} of {res.rows.length} {noun} have marked answers.
          </div>
          {!res.comparative && <div class="text-[12.5px] text-[var(--color-text-muted)]" data-comparative-na>Strongest and weakest are not shown: there is only one to compare.</div>}
          {tab === 'item' ? <ItemTable rows={res.rows as ItemTableRow[]} meaning={A.meaning} /> : <BucketTable rows={res.rows as BucketRow[]} name={tab} />}
          <Card title="Likely causes" sub="For each weak area: the mistakes students made most often">
            {res.diagnostics.length === 0 ? <div class="text-[13px] text-[var(--color-text-muted)]">Nothing is weak here on the marked answers so far.</div> : (
              <ul class="flex flex-col gap-2 text-[12.5px]">
                {res.diagnostics.map((d) => (
                  <li key={d.bucket} class="text-[var(--color-text)]">
                    <b>{d.bucket}:</b>{' '}
                    {d.limitations.length ? d.limitations.map((l, i) => <span key={l.code}>{i ? '; ' : ''}{l.meaning} ({l.count})<CodeTag code={l.code} /></span>) : 'no common mistake recorded'}
                    {d.zeroMarkCommand && <span class="text-[var(--color-text-muted)]">. Zero marks came most often on "{d.zeroMarkCommand.command}" questions ({d.zeroMarkCommand.count})</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
      {res.applicable && <NoteEditor a={a} tab={tab} note={note} finalised={finalised} />}
      <div class="h-6" />
    </TeachingPage>
  );
}
